import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { getPublicConfig } from "../../api/config/getPublicConfig";
import { getDossier } from "../../api/dossier/getDossier";
import { getQuote } from "../../api/quote/getQuote";
import { getQuoteDealerPricingRequests } from "../../api/quote/getQuoteDealerPricingRequests";
import { selectDealerPricingRequest } from "../../api/quote/selectDealerPricingRequest";
import APSelectDropDown from "../../components/APSelectDropDown";
import APButton from "../../components/buttons/APButton";
import BackButton from "../../components/buttons/BackButton";
import PageContainer from "../../components/PageContainer";
import { getCRMClientURL } from "../../helpers/configuration";
import { canRequestDealerPricing } from "../../helpers/dossiers";
import {
  calculatePricingRequestAverages,
  getDefaultVehiclePricingRequestSearchParams,
  groupVehiclePricingRequested,
  sortVehiclePricingRequests,
  VehiclePricingRequestSearchSortOption,
  VehiclePricingRequestSearchSortOptionsList,
} from "../../helpers/vehiclePricingRequests";
import { useAuth } from "../../providers/AuthProvider";
import { ErrorCode } from "../../types/Error";
import { VehiclePricingRequest } from "../../types/VehiclePricingRequest";
import PricingRequestAverages from "../requestVehiclePricing/components/PricingRequestAverages";
import PricingRequestList from "../requestVehiclePricing/components/PricingRequestList";

const SelectDealerPricing = () => {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const { getScopedPageUrl } = useAuth();
  const [sortOptionId, setSortOptionId] =
    useState<VehiclePricingRequestSearchSortOption>("onroadPriceAsc");
  if (!quoteId) {
    throw new Error("quote id param is missing");
  }

  const quote = getQuote(quoteId, {
    mutationOptions: {
      errorNavPaths: { [ErrorCode.QuoteDeleted]: getScopedPageUrl("home") },
    },
  });
  const dossierId = quote.data?.identifiers.crmDossierId;
  const dossier = getDossier(dossierId || "", { disabled: !dossierId });
  const dealerPricingRequests = getQuoteDealerPricingRequests(quoteId);
  const config = getPublicConfig();
  const crmClientURL = config.isSuccess ? getCRMClientURL(config.data) : null;

  const {
    mutateAsync: selectDealerPricingRequestAsync,
    isLoading: isSelectingDealerPricingRequest,
  } = selectDealerPricingRequest(quoteId, {
    mutationOptions: {
      successAlertMessage: "Quote selected",
    },
  });

  if (quote.isError) {
    throw quote.error;
  }

  if (dossier.isError) {
    throw dossier.error;
  }

  const { requested: requestedPricing, received: receivedPricing } = useMemo(
    () => groupVehiclePricingRequested(dealerPricingRequests.data ?? []),
    [dealerPricingRequests.data]
  );

  const onUseQuote = async (pricingRequest: VehiclePricingRequest) => {
    await selectDealerPricingRequestAsync({
      pricingRequestId: pricingRequest.id,
    });
  };

  const onVehiclePricingRequestSortChange = (
    optionId: VehiclePricingRequestSearchSortOption
  ) => {
    setSortOptionId(optionId);
  };

  const sortDropdown = (
    <Box
      sx={{
        width: {
          xs: "168px",
          sm: "190px",
        },
        display: "flex",
      }}
    >
      <APSelectDropDown
        label="Sort By"
        selectedOptionId={sortOptionId}
        options={VehiclePricingRequestSearchSortOptionsList.filter(
          (option) => option.id !== "selectedDesc"
        )}
        onOptionChange={onVehiclePricingRequestSortChange}
      />
    </Box>
  );

  const isLoading =
    quote.isLoading ||
    dossier.isLoading ||
    config.isLoading ||
    dealerPricingRequests.isLoading ||
    isSelectingDealerPricingRequest;
  const isSuccess =
    quote.isSuccess &&
    dossier.isSuccess &&
    config.isSuccess &&
    dealerPricingRequests.isSuccess;

  // Check if pricing requests are allowed to be selected
  const allowSelectPricingRequests =
    dossier.data &&
    quote.data &&
    canRequestDealerPricing(dossier.data, quote.data);

  return (
    <PageContainer
      title="Select Vehicle Pricing"
      loading={isLoading}
      size="full"
      titleButton={sortDropdown}
    >
      {isSuccess && (
        <Stack spacing={2}>
          <Box>
            <Typography variant="labelLarge">
              {dossier.data.crmContactName} - {quote.data.vehicle.year}{" "}
              {quote.data.vehicle.make} {quote.data.vehicle.model}{" "}
              {quote.data.vehicle.variantName}
            </Typography>
          </Box>

          <Stack
            spacing={0}
            sx={{
              display: "grid",
              gap: 8,
              gridTemplateColumns: { md: "2fr 1fr" },
            }}
          >
            <Stack spacing={2}>
              <PricingRequestList
                displayType="accordion"
                cardType="quote-specific"
                requests={sortVehiclePricingRequests(
                  receivedPricing,
                  sortOptionId
                )}
                type="received"
                crmClientURL={crmClientURL}
                columns="double"
                showUseQuoteButton={allowSelectPricingRequests}
                onUseQuote={onUseQuote}
              />
              <PricingRequestAverages
                aggregations={calculatePricingRequestAverages(receivedPricing)}
              />
            </Stack>
            <Box>
              <PricingRequestList
                displayType="accordion"
                cardType="quote-specific"
                requests={requestedPricing}
                type="requested"
                crmClientURL={crmClientURL}
                defaultExpanded={false}
              />
            </Box>
          </Stack>

          <Box sx={{ display: "flex", gap: 2 }}>
            <BackButton />
            <APButton
              variant="contained"
              text={`All ${quote.data.vehicle.make} ${quote.data.vehicle.model} Pricing`}
              onClick={() =>
                navigate(
                  `${getScopedPageUrl(
                    "searchDealerPricingRequests"
                  )}?${getDefaultVehiclePricingRequestSearchParams(
                    quote.data
                  ).toString()}`
                )
              }
            />{" "}
          </Box>
        </Stack>
      )}
    </PageContainer>
  );
};

export default SelectDealerPricing;
